// src/pages/Admin/Casting/components/MessagesPanel.jsx
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaSave,
  FaTrash,
  FaBullhorn,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const EMPTY = {
  id: null,
  type: "INFO",
  title: "",
  content: "",
  target_mat_number: null, // null = global
  severity: "",
  is_pinned: false,
  is_active: true,
};

export default function MessagesPanel({
  eventId,
  messages,
  setMessages,
  castState,
  setCastState,
  onSetMode,
  services,
}) {
  const {
    createEventCastMessage,
    updateEventCastMessage,
    toggleEventCastMessageActive,
    deleteEventCastMessage,
    upsertEventCastState,
  } = services;

  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(
    () => messages.find((m) => m.id === draft.id) || null,
    [messages, draft.id]
  );

  const startNew = () => setDraft(EMPTY);

  const selectMsg = (m) =>
    setDraft({
      id: m.id,
      type: m.type || "INFO",
      title: m.title || "",
      content: m.content || "",
      target_mat_number:
        m.target_mat_number === undefined ? null : m.target_mat_number,
      severity: m.severity || "",
      is_pinned: Boolean(m.is_pinned),
      is_active: Boolean(m.is_active),
    });

  const save = async () => {
    if (!eventId) return;
    if (!draft.content?.trim()) {
      toast.error("Le contenu du message est obligatoire.");
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        const updated = await updateEventCastMessage(eventId, draft);
        if (!updated) return;

        setMessages((prev) =>
          (prev || []).map((x) => (x.id === updated.id ? updated : x))
        );
        toast.success("Message mis à jour.");
      } else {
        const created = await createEventCastMessage(eventId, draft);
        if (!created) return;

        setMessages((prev) => [created, ...(prev || [])]);
        setDraft((s) => ({ ...s, id: created.id }));
        toast.success("Message créé.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'enregistrement du message.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      const r = await toggleEventCastMessageActive(eventId, m.id);
      if (!r) return;

      setMessages((prev) =>
        (prev || []).map((x) =>
          x.id === m.id ? { ...x, is_active: Boolean(r.is_active) } : x
        )
      );

      if (draft.id === m.id) {
        setDraft((s) => ({ ...s, is_active: Boolean(r.is_active) }));
      }
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du toggle du message.");
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      const r = await deleteEventCastMessage(eventId, m.id);
      if (!r) return;

      setMessages((prev) => (prev || []).filter((x) => x.id !== m.id));
      if (draft.id === m.id) setDraft(EMPTY);
      toast.success("Message supprimé.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const setLive = async (m) => {
    try {
      // passe en mode MESSAGE + message actif
      const st = await upsertEventCastState(eventId, {
        mode: "MESSAGE",
        active_message_id: m.id,
      });
      if (!st) return;

      setCastState(st);
      onSetMode?.("MESSAGE");
      toast.success("Message diffusé sur l’écran.");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de diffuser le message.");
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Liste */}
      <div className="lg:col-span-1 rounded-3xl bg-white/80 dark:bg-slate-900/70 backdrop-blur ring-1 ring-black/5 dark:ring-white/10 shadow-xl p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
            Messages
          </div>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
          >
            <FaPlus /> Nouveau
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {(messages || []).length === 0 ? (
            <div className="text-sm text-slate-600 dark:text-slate-300">
              Aucun message pour cet événement.
            </div>
          ) : (
            messages.map((m) => {
              const isSelected = draft.id === m.id;
              const isLive = castState?.mode === "MESSAGE" && castState?.active_message_id === m.id;

              return (
                <button
                  key={m.id}
                  onClick={() => selectMsg(m)}
                  className={[
                    "w-full text-left rounded-2xl p-3 ring-1 transition",
                    isSelected
                      ? "bg-sky-600 text-white ring-sky-600"
                      : "bg-white/70 dark:bg-slate-900/50 text-slate-900 dark:text-white ring-slate-200 dark:ring-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-bold text-sm truncate">
                      {m.title || "(Sans titre)"}
                    </div>
                    <div className="flex items-center gap-2 text-xs opacity-90">
                      {m.is_active ? <FaCheckCircle /> : <FaTimesCircle />}
                      {isLive ? (
                        <span className="px-2 py-0.5 rounded-full bg-white/20">
                          LIVE
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="text-xs opacity-90 mt-1 line-clamp-2">
                    {String(m.content || "").replace(/<[^>]*>/g, "")}
                  </div>
                  <div className="text-[11px] opacity-80 mt-1">
                    {m.target_mat_number ? `Tapis ${m.target_mat_number}` : "Global"}
                    {m.type ? ` • ${m.type}` : ""}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Éditeur */}
      <div className="lg:col-span-2 rounded-3xl bg-white/80 dark:bg-slate-900/70 backdrop-blur ring-1 ring-black/5 dark:ring-white/10 shadow-xl p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
              {draft.id ? "Modifier le message" : "Créer un message"}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Message global ou ciblé sur un tapis.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {draft.id ? (
              <button
                onClick={() => selected && setLive(selected)}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                disabled={!draft.id}
                title="Diffuser ce message sur l’écran"
              >
                <FaBullhorn /> Diffuser
              </button>
            ) : null}

            <button
              onClick={save}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-2xl bg-sky-600 hover:bg-sky-700 text-white font-semibold disabled:opacity-60"
              disabled={saving}
            >
              <FaSave /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
              Type
            </label>
            <select
              value={draft.type}
              onChange={(e) => setDraft((s) => ({ ...s, type: e.target.value }))}
              className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="INFO">INFO</option>
              <option value="ALERT">ALERT</option>
              <option value="PODIUM">PODIUM</option>
              <option value="CALL">CALL</option>
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
              Cible
            </label>
            <select
              value={draft.target_mat_number ?? ""}
              onChange={(e) =>
                setDraft((s) => ({
                  ...s,
                  target_mat_number: e.target.value ? Number(e.target.value) : null,
                }))
              }
              className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="">Global</option>
              {Array.from({ length: 10 }).map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  Tapis {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
              Titre
            </label>
            <input
              value={draft.title}
              onChange={(e) => setDraft((s) => ({ ...s, title: e.target.value }))}
              className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="Ex : Remise des médailles"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-300">
              Contenu *
            </label>
            <textarea
              value={draft.content}
              onChange={(e) =>
                setDraft((s) => ({ ...s, content: e.target.value }))
              }
              rows={6}
              className="mt-1 w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="Ex : Tous les compétiteurs sont attendus sur l'aire de remise des médailles…"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={Boolean(draft.is_active)}
                onChange={(e) => setDraft((s) => ({ ...s, is_active: e.target.checked }))}
              />
              Actif
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={Boolean(draft.is_pinned)}
                onChange={(e) => setDraft((s) => ({ ...s, is_pinned: e.target.checked }))}
              />
              Épinglé
            </label>

            {draft.id ? (
              <button
                onClick={() => selected && toggleActive(selected)}
                className="ml-auto inline-flex items-center gap-2 h-9 px-3 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white text-sm font-semibold"
              >
                Toggle actif
              </button>
            ) : null}

            {draft.id ? (
              <button
                onClick={() => selected && remove(selected)}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
              >
                <FaTrash /> Supprimer
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
